import { useState, useEffect } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BarChart3, TrendingUp, Globe, Package, Gift, Users } from "lucide-react";
import api from "@/lib/api";

const COLORS = ["#3861FB", "#60A5FA", "#818CF8", "#A78BFA", "#C084FC", "#38BDF8", "#2DD4BF", "#4ADE80", "#FBBF24", "#F87171"];

function ChartCard({ title, icon: Icon, children, testId }) {
  return (
    <div className="glass-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5 text-blue-400" />
        </div>
        <span className="text-xs font-semibold text-slate-400">{title}</span>
      </div>
      {children}
    </div>
  );
}

export default function Analytics() {
  const [dailyBookings, setDailyBookings] = useState([]);
  const [dailyPax, setDailyPax] = useState([]);
  const [topOptions, setTopOptions] = useState([]);
  const [topExtras, setTopExtras] = useState([]);
  const [countries, setCountries] = useState([]);
  const [addonConversion, setAddonConversion] = useState([]);
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [db, dp, to, te, cd, ac] = await Promise.all([
          api.get(`/analytics/daily-bookings?days=${days}`),
          api.get(`/analytics/daily-pax?days=${days}`),
          api.get("/analytics/top-options"),
          api.get("/analytics/top-extras"),
          api.get("/analytics/country-distribution"),
          api.get("/analytics/addon-conversion"),
        ]);
        setDailyBookings(db.data.data || []);
        setDailyPax(dp.data.data || []);
        setTopOptions(to.data.data || []);
        setTopExtras(te.data.data || []);
        setCountries(cd.data.data || []);
        setAddonConversion(ac.data.data || []);
      } catch (e) {
        console.error("Analytics fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [days]);

  const formatDate = (d) => {
    if (!d) return "";
    const parts = d.split("-");
    return `${parts[2]}/${parts[1]}`;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Analitik</h1>
          <p className="text-sm text-slate-500 mt-0.5">Rezervasyon verileri ve trendler</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger data-testid="analytics-period-select" className="w-[140px] rounded-lg text-sm border-white/10 bg-black/30 text-slate-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Son 7 Gun</SelectItem>
            <SelectItem value="14">Son 14 Gun</SelectItem>
            <SelectItem value="30">Son 30 Gun</SelectItem>
            <SelectItem value="90">Son 90 Gun</SelectItem>
            <SelectItem value="365">Son 1 Yil</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="glass-card p-5 h-[300px]">
                <div className="h-4 bg-white/5 animate-pulse w-40 rounded mb-4" />
                <div className="h-48 bg-white/[0.02] animate-pulse rounded-lg" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Daily Bookings */}
            <ChartCard title="Gunluk Rezervasyon Sayisi" icon={BarChart3} testId="chart-daily-bookings">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={dailyBookings}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
                  <Tooltip
                    labelFormatter={(v) => v}
                    contentStyle={{ fontSize: 12, background: "#0F1629", border: "1px solid rgba(56,97,251,0.15)", borderRadius: 8, color: "#E2E8F0" }}
                  />
                  <Bar dataKey="count" fill="#3861FB" name="Rezervasyon" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Daily Pax */}
            <ChartCard title="Gunluk Yolcu Sayisi" icon={TrendingUp} testId="chart-daily-pax">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={dailyPax}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
                  <Tooltip contentStyle={{ fontSize: 12, background: "#0F1629", border: "1px solid rgba(56,97,251,0.15)", borderRadius: 8, color: "#E2E8F0" }} />
                  <Line type="monotone" dataKey="total" stroke="#3861FB" strokeWidth={2} name="Toplam" dot={false} />
                  <Line type="monotone" dataKey="adult" stroke="#60A5FA" strokeWidth={1.5} name="Yetiskin" dot={false} />
                  <Line type="monotone" dataKey="child" stroke="#818CF8" strokeWidth={1.5} name="Cocuk" dot={false} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Top Options */}
            <ChartCard title="En Cok Satan Urunler" icon={Package} testId="chart-top-options">
              {topOptions.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={topOptions} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }} />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tick={{ fontSize: 9, fill: "#94A3B8" }}
                      width={120}
                      tickFormatter={(v) => v.length > 18 ? v.slice(0, 18) + "..." : v}
                    />
                    <Tooltip contentStyle={{ fontSize: 12, background: "#0F1629", border: "1px solid rgba(56,97,251,0.15)", borderRadius: 8, color: "#E2E8F0" }} />
                    <Bar dataKey="count" fill="#3861FB" name="Satis" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-slate-600">Veri yok</div>
              )}
            </ChartCard>

            {/* Top Extras */}
            <ChartCard title="En Cok Satan Ekstralar" icon={Gift} testId="chart-top-extras">
              {topExtras.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={topExtras} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }} />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tick={{ fontSize: 9, fill: "#94A3B8" }}
                      width={120}
                      tickFormatter={(v) => v.length > 18 ? v.slice(0, 18) + "..." : v}
                    />
                    <Tooltip contentStyle={{ fontSize: 12, background: "#0F1629", border: "1px solid rgba(56,97,251,0.15)", borderRadius: 8, color: "#E2E8F0" }} />
                    <Bar dataKey="count" fill="#818CF8" name="Adet" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-slate-600">Veri yok</div>
              )}
            </ChartCard>

            {/* Country Distribution */}
            <ChartCard title="Ulke Dagilimi" icon={Globe} testId="chart-countries">
              {countries.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={countries}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      dataKey="count"
                      nameKey="country"
                      label={({ country, percent }) =>
                        `${country} ${(percent * 100).toFixed(0)}%`
                      }
                      labelLine={false}
                    >
                      {countries.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, background: "#0F1629", border: "1px solid rgba(56,97,251,0.15)", borderRadius: 8, color: "#E2E8F0" }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-slate-600">Ulke verisi yok</div>
              )}
            </ChartCard>

            {/* Addon Conversion */}
            <ChartCard title="Addon Donusum Orani" icon={Users} testId="chart-addon-conversion">
              {addonConversion.length > 0 ? (
                <div className="space-y-3">
                  {addonConversion.map((a, i) => (
                    <div key={i} className="bg-black/20 border border-white/[0.04] rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-white">{a.addon}</span>
                        <span className="text-sm font-bold text-blue-400">{a.rate}%</span>
                      </div>
                      <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all"
                          style={{ width: `${Math.min(a.rate, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-[10px] text-slate-600">
                        <span>{a.sent} gonderildi</span>
                        <span>{a.converted} donustu</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-slate-600">
                  Henuz addon teklifi gonderilmedi
                </div>
              )}
            </ChartCard>
          </div>
        )}
      </div>
    </div>
  );
}
